<template>

  <div class="container">

    <BreadcrumSection :part1="$t('Assignatures')"></BreadcrumSection>

    <h1>{{ $t("Assignatures") }}</h1>

    <div v-if="totalElements === 0" class="alert alert-danger rounded-0" role="alert">
      {{ $t("No results") }}
    </div>

    <div v-else class="row">

      <div class="col-sm-12 col-md-8" >

        <div class="row">
          <div class="col-sm-12 col-md-8">
            <PaginationTable
                class="container"
                :show-pagination="true"
                v-model:current-page="currentPage"
                v-model:page-size="pageSize"
                :total="totalElements"
                :handle-current-change="handleCurrentChange">
            </PaginationTable>
          </div>

          <div class="col-sm-12 col-md-4">
            <div class="form-control-sm">
              <div class="d-flex align-items-center justify-content-end">
                <label class="form-check-label me-1" for="selectOrderBy">{{ $t("Ordenació") }}:</label>
                <select class="form-select form-select-sm select-auto-width" @change="handleOrderChange(selectedOrder)" v-model="selectedOrder" id="selectOrderBy">
                  <option value="assignatura_nom_ca asc" selected>{{$t('Nom')}} asc.</option>
                  <option value="assignatura_nom_ca desc">{{$t('Nom')}} desc.</option>
                </select>
              </div>
            </div>
          </div>

        </div>


        <ul class="list-group list-group-flush mt-1" >
          <li>
            <router-link-locale
                v-for="(assignatura) in response.assignatures"
                :key="assignatura.id"
                :to="{name: 'assignatura', params: {id: assignatura.id, listPage: currentPage, listSort: selectedOrder}}"
                class="list-group-item list-group-item-action flex-column align-items-start p-2"
                tag="li"
            >

              <div class="mb-1">
                <span v-if="assignatura[`assignatura_nom_${locale}`]" class="text-blue">{{ assignatura[`assignatura_nom_${locale}`] }}</span>
                <span v-else class="text-blue">{{ assignatura.assignatura_nom_ca }}</span>
              </div>

              <div class="fw-bolder mb-1">
                <span>{{ assignatura.assignatura_sigles }}</span>
              </div>

              <div class="mb-1" v-if="assignatura.assignatura_centres">
                <span v-for="centre in (assignatura.assignatura_centres.filter((valor, indiceActual, arreglo) => arreglo.indexOf(valor) === indiceActual))" v-bind:key="centre">
                  {{ $t(centre) }}
                </span>
              </div>

              <div class="mt-2 text-secondary " v-if="assignatura.assignatura_titulacions">

                <div v-for="(titulacio, index) in assignatura.assignatura_titulacions" v-bind:key="titulacio">
                  <div class="d-flex w-100 justify-content-between">
                    <span>{{ $t(titulacio) }}</span>
                    <span v-if="getTipusAssignatura(assignatura.assignatura_tipus, index) === 'OB'" class="mb-1"><span class="badge badge-upc">{{ $t('OB') }}</span></span>
                    <span v-else-if="getTipusAssignatura(assignatura.assignatura_tipus, index) === 'OP'" class="mb-1"><span class="badge badge-upc-2">{{ $t('OP') }}</span></span>
                    <span v-else-if="getTipusAssignatura(assignatura.assignatura_tipus, index) === 'ALE'" class="mb-1"> <span class="badge bg-danger">{{ $t('ALE') }}</span></span>
                    <span v-else class="mb-1"><span class="badge bg-dark">{{ $t('OTHER') }}</span></span>
                  </div>
                </div>
              </div>
            </router-link-locale>
          </li>
        </ul>

        <PaginationTable
            class="container mt-3"
            :show-pagination="true"
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :total="totalElements"
            :handle-current-change="handleCurrentChange">
        </PaginationTable>

      </div>

      <div v-if="response.facetes" id="filtres" class="col-sm-12 col-md-4 mt-0">
        <FacetesFilter :data="response.facetes"  @apply-filter="handleApplyFilter" split-index="2" :marc="true"></FacetesFilter>
      </div>
    </div>

  </div>

</template>

<script>

import {computed, onMounted, ref} from "vue";
  import store from "@/store";
  import BreadcrumSection from "@/components/sections/BreadcrumbSection.vue";
  import RouterLinkLocale from "@/components/RouterLinkLocale.vue";
  import PaginationTable from "@/components/PaginationTable.vue";
  import FacetesFilter from "@/components/FacetesFilter.vue";
import {setMetadata} from "@/composables/metadataFunctions";
import {
  createHandleApplyFilter,
  createHandleCurrentChange,
  createHandleOrderChange,
  getAssignatures, getTipusAssignatura
} from "@/composables/pageFunctions";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {usePageWatcher, useQueryWatcher, useSortWatcher} from "@/composables/useWatchers";


  export default {
    name: "AssignaturesView",
    methods: {getTipusAssignatura},
    components: {FacetesFilter, PaginationTable, RouterLinkLocale, BreadcrumSection},

    setup(){
      const { locale } = useI18n();
      const response = ref([]);

      const query = ref("tipus:assignatura")
      const initQuery = query.value
      const currentPage = ref(1);
      const totalElements = ref(1)
      const pageSize = computed(() => { return store.getters.pageSize})
      const selectedOrder = ref("assignatura_nom_ca asc");

      const route = useRoute();
      const router = useRouter();


      const { t } = useI18n();

      onMounted(async () => {
        setMetadata(t('Assignatures'))
        await store.dispatch('updateSearchSelected', "Assignatura");
        await store.dispatch('updateBackgroundImage', { view: route.name, url: require('@/assets/images/sections/banner-green.webp') });
        currentPage.value = parseInt(route.params.page) || 1;
        const result = await getAssignatures(currentPage.value, store.getters.pageSize, query.value, route.params.sort, route.query)
        response.value = result
        totalElements.value = result.total
      });

      const handleCurrentChange = createHandleCurrentChange("assignatures", router, route);
      const handleOrderChange = createHandleOrderChange("assignatures", router, route, currentPage);

      usePageWatcher(getAssignatures, currentPage, response, totalElements, query, route, store);
      useSortWatcher(getAssignatures, currentPage, response, totalElements, query, route, store);
      useQueryWatcher(getAssignatures, currentPage, response, totalElements, query, route, store);

      const handleApplyFilter = async (newQuery) => {
        await createHandleApplyFilter({
          newQuery,
          callFunction:getAssignatures,
          currentPage,
          query,
          initQuery,
          response,
          totalElements,
          route,
          store
        });
      };

      return {
        response,
        query,
        locale,

        selectedOrder,
        currentPage,
        totalElements,
        pageSize,

        handleCurrentChange,
        handleApplyFilter,
        handleOrderChange

      }

    }
  }

</script>

<style scoped>

</style>
